/**
 * Photos and videos in one recap selection (VIDEO_PLAN §4, decided 2026-10-15).
 *
 * The Recap composer keeps ONE ordered list of what's picked. Photos are their
 * integer ids, as they always were; a video is the string key `v:<id>` (its
 * pool item's `id`, see videoUploadService._tripVideoItem). Everything that
 * works on photos alone — tagging, audience, delete, captions, the cover —
 * already deals in numbers, so a video key simply never matches there.
 *
 * On the wire the server gets `photoIds` (unchanged meaning, photos only) and
 * `media` ([{ kind, id }] in the picked order), which is how it orders photos
 * and videos together and how it knows a video was taken OUT.
 */

export const isVideoKey = (id) => typeof id === 'string' && id.startsWith('v:');

export const videoKey = (videoId) => `v:${videoId}`;

export const videoIdOf = (key) => Number(String(key).slice(2));

/** Draft body for PUT /api/albums/trip/:tripId/draft, from composer state. */
export function toDraftBody(draftData) {
  if (!draftData || !Array.isArray(draftData.photoIds)) return draftData;
  const selection = draftData.photoIds;
  const body = {
    ...draftData,
    photoIds: selection.filter(id => !isVideoKey(id)),
    media: selection.map(id => (isVideoKey(id) ? { kind: 'video', id: videoIdOf(id) } : { kind: 'photo', id }))
  };
  if (isVideoKey(body.coverPhotoId)) body.coverPhotoId = null;
  if (body.photoCaptions && typeof body.photoCaptions === 'object') {
    body.photoCaptions = Object.fromEntries(Object.entries(body.photoCaptions).filter(([k]) => !isVideoKey(k)));
  }
  return body;
}

/** The saved selection of a draft, photos and videos in their stored order. */
export function draftSelection(draft) {
  const photos = (draft?.photos || []).map((p, i) => ({ id: p.id, order: p.display_order ?? i + 1 }));
  const videos = (draft?.videos || []).map(v => ({ id: v.id, order: v.display_order ?? Number.MAX_SAFE_INTEGER }));
  return [...photos, ...videos]
    .map((item, i) => ({ ...item, i }))
    .sort((a, b) => a.order - b.order || a.i - b.i)
    .map(item => item.id);
}
